// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — passkeys in the browser (decision O4): turning the site's JSON
 * options into what `navigator.credentials` takes (byte fields as
 * ArrayBuffers) and its answers back into JSON (base64url), for
 * registration (Account → Passkeys) and for step-up. The site verifies
 * everything (`functions/api/data/_passkeys.ts`); nothing is kept here.
 */

import { fromBase64Url, toBase64Url } from './ownDataCrypto'

const buf = (s: string): ArrayBuffer => {
  const b = fromBase64Url(s)
  if (!b) throw new Error('bad base64url')
  return b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer
}
const b64 = (b: ArrayBuffer | null): string => (b ? toBase64Url(new Uint8Array(b)) : '')

/** Whether this browser can use passkeys at all. */
export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential === 'function' && !!navigator.credentials
}

interface JsonCredDesc { type: 'public-key'; id: string }

export interface CreationJson {
  challenge: string
  rp: { id: string; name: string }
  user: { id: string; name: string; displayName: string }
  pubKeyCredParams: Array<{ type: 'public-key'; alg: number }>
  authenticatorSelection?: AuthenticatorSelectionCriteria
  attestation?: AttestationConveyancePreference
  excludeCredentials?: JsonCredDesc[]
  timeout?: number
}

export interface RequestJson {
  challenge: string
  rpId: string
  allowCredentials?: JsonCredDesc[]
  userVerification?: UserVerificationRequirement
  timeout?: number
}

/** Make a passkey; answers the credential as JSON for `POST /api/data/passkeys`, or the browser's error name. */
export async function createPasskey(o: CreationJson): Promise<{ ok: true; credential: Record<string, unknown> } | { ok: false; error: string }> {
  try {
    const cred = (await navigator.credentials.create({
      publicKey: {
        ...o,
        challenge: buf(o.challenge),
        user: { ...o.user, id: buf(o.user.id) },
        excludeCredentials: (o.excludeCredentials ?? []).map((c) => ({ type: c.type, id: buf(c.id) })),
      },
    })) as PublicKeyCredential | null
    if (!cred) return { ok: false, error: 'cancelled' }
    const r = cred.response as AuthenticatorAttestationResponse
    return { ok: true, credential: { id: cred.id, rawId: b64(cred.rawId), type: cred.type, response: { clientDataJSON: b64(r.clientDataJSON), attestationObject: b64(r.attestationObject) } } }
  } catch (err) {
    return { ok: false, error: (err as { name?: string })?.name ?? 'failed' }
  }
}

/** Sign a step-up challenge with a passkey; answers the assertion as JSON, or the browser's error name. */
export async function usePasskey(o: RequestJson): Promise<{ ok: true; assertion: Record<string, unknown> } | { ok: false; error: string }> {
  try {
    const cred = (await navigator.credentials.get({
      publicKey: { ...o, challenge: buf(o.challenge), allowCredentials: (o.allowCredentials ?? []).map((c) => ({ type: c.type, id: buf(c.id) })) },
    })) as PublicKeyCredential | null
    if (!cred) return { ok: false, error: 'cancelled' }
    const r = cred.response as AuthenticatorAssertionResponse
    return {
      ok: true,
      assertion: { id: cred.id, rawId: b64(cred.rawId), type: cred.type, response: { clientDataJSON: b64(r.clientDataJSON), authenticatorData: b64(r.authenticatorData), signature: b64(r.signature), userHandle: b64(r.userHandle) } },
    }
  } catch (err) {
    return { ok: false, error: (err as { name?: string })?.name ?? 'failed' }
  }
}
